// InputWithFloatingLabel.js

/*
FloatingLabelContainer: The container that holds both the input and the label, ensuring the label can float above the input field.
FloatingLabel: The label text that moves above the input field when the input is focused or has a value. It uses CSS transitions for smooth movement.
**/

import { useState, useEffect } from "react";
import { Box, TextInput, Text } from "grommet";
import { updateStateHandler } from "../Utils/Utils";
import styled from "styled-components";
import isPropValid from '@emotion/is-prop-valid'; // Install: npm i @emotion/is-prop-valid

// Styled container for the floating label and input
const FloatingLabelContainer = styled(Box)`
    position: relative;
    margin: 5px 0;
`;

// Styled floating label that moves based on focus and value presence
// const FloatingLabel = styled.label`

const FloatingLabel = styled.label.withConfig({
  shouldForwardProp: (prop) =>
    isPropValid(prop) && !["isFocused", "hasValue", "disable"].includes(prop),
})`
    position: absolute;
    left: 10px;
    top: ${(props) => (props.hasValue || (!props.disable && props.isFocused) ? '-8px' : '50%')};
    transform: translateY(${(props) => (props.hasValue || (!props.disable && props.isFocused) ? '0' : '-50%')});
    font-size: ${(props) => (props.hasValue || (!props.disable && props.isFocused) ? '12px' : '15px')};
    // color: ${(props) => (props.isFocused ? '#7D4CDB' : '#666')};
    transition: all 0.2s ease-out;
    pointer-events: none;
    background-color: white;
    padding: 0 4px;
    z-index: 1;
`;

// Styled TextInput that supports a floating label
const StyledTextInput = styled(TextInput)`
    padding: 15px 10px 2px 10px;
    font-size: 16px;
    // border: 1px solid #ccc; /* Ensure border is always visible */
    border-radius: 4px;
    &:focus {
        border-color: '#7D4CDB';
        box-shadow: '0 0 2px 2px #FF0000';
    }
    // background-color: ${(props) => (props.readOnly ? '#f5f5f5' : 'white')}; /* Grey out the background when disabled */
    // color: ${(props) => (props.readOnly ? '#666' : 'black')}; /* Dim the text color when disabled */
    // marrgin: 20px;
    `;

const InputWithFloatingLabel = (props) => {
    const [inputValue, setInputValue] = useState('');  // State for managing the input value
    const [isFocused, setIsFocused] = useState(false);  // State for managing input focus

    // console.log('In InputWithFloatingLabel props obj:', props);

    // Effect to set initial value based on props and ensure it's displayed as a float with two decimals
    useEffect(() => {
        if (props.initValue !== undefined && props.initValue !== null && props.initValue !== 0 && props.initValue !== "") {
            const formattedValue = props.number ? parseFloat(props.initValue).toFixed(2) : props.initValue;
            setInputValue(formattedValue);
        } else {
            setInputValue(""); // If the value is 0 or empty, keep it blank
        }
    //}, [props.initValue, props.number]);
    }, []);




    // Function to format the value to 2 decimal places on blur
    const formatValueToTwoDecimals = (value) => {
        if (props.number) {
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                return numValue.toFixed(2);  // Ensure 2 decimal places
            }
        }
        return value;
    };

    // Function to handle value updates without forcing format
    const updateValue = (value) => {
        if (props.number) {
            // Allow normal typing, but restrict to valid decimal input
            const regex = /^\d*\.?\d{0,2}$/;
            if (!regex.test(value)) {
                return;
            }
        }

        setInputValue(value);

        updateStateHandler(props.stateHandler, props.fieldName, props.number ? Number(value) : value);  // Update state       
    
        // console.log('prev Value >> ', props.initValue);
        // console.log('new value >> ', Number(inputValue)); 

        if(props.updateProgressByCheck){
            props.updateProgressByCheck(Number(props.initValue), Number(value));
        }      
    };

    // Handle formatting when input loses focus
    const handleBlur = () => {
        const formattedValue = formatValueToTwoDecimals(inputValue);
        setInputValue(formattedValue);  // Set formatted value
        // updateStateHandler(props.stateHandler, props.fieldName, props.number ? Number(formattedValue) : formattedValue);  // Update state       
    
        // // console.log('prev Value >> ', props.initValue);
        // // console.log('new value >> ', Number(inputValue));

        // if(props.updateProgressByCheck){
        //     props.updateProgressByCheck(Number(props.initValue), Number(inputValue));
        // }

    };

    return (
        <Box>
            {/* Container for the floating label and input */}
            <FloatingLabelContainer>
                {/* Floating label that moves based on input state */}
                <FloatingLabel
                    htmlFor={props.fieldName}
                    isFocused={isFocused}
                    hasValue={inputValue !== ""}
                    disable={props.disable}
                >
                    {props.text}
                </FloatingLabel>

                {/* Styled TextInput with dynamic value and focus handling */}
                <StyledTextInput
                    id={props.fieldName}
                    type={props.number ? "number" : "text"}
                    value={inputValue}
                    readOnly={props.disable}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}  // Format value on blur
                    onChange={(event) => updateValue(event.target.value)}  // Allow normal typing
                    // onChange={(event) => updateValue(event.target.value)} // Update state on each change
                />
            </FloatingLabelContainer>

            {/* Subtext below the input with margin top */}
            <Text alignSelf="end" color="brand" size="small" margin={{ top: '-5px' }}>
                {props.subText}
            </Text>
        </Box>
    );
};

export default InputWithFloatingLabel;
